import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGetLeads, apiDeleteLead, apiGetCounselors, apiExportLeads } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const STATUSES = ['', 'New', 'Contacted', 'Follow-up', 'Will Visit', 'Visited', 'Converted', 'Not Interested'];
const SOURCES  = ['', 'Facebook Ads', 'Instagram Ads', 'Website', 'Walk-in', 'Referral'];

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function LeadsPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [leads, setLeads] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(1);
    const [page, setPage]   = useState(1);
    const [loading, setLoading] = useState(true);
    const [counselors, setCounselors] = useState([]);
    const [deleting, setDeleting] = useState(null);
    const [exporting, setExporting] = useState(false);

    const [filters, setFilters] = useState({
        search: '', status: '', source: '', counselor_id: '',
    });

    const load = useCallback(async (pg = page) => {
        setLoading(true);
        try {
            const res = await apiGetLeads({ page: pg, limit: 15, ...filters });
            setLeads(res.data.data);
            setTotal(res.data.total);
            setPages(res.data.pages);
        } catch {
            toast.error('Failed to fetch leads');
        } finally {
            setLoading(false);
        }
    }, [page, filters]);

    useEffect(() => { load(page); }, [page, filters]); // eslint-disable-line

    useEffect(() => {
        if (user?.role === 'admin') {
            apiGetCounselors().then(r => setCounselors(r.data?.data || [])).catch(() => {});
        }
    }, [user]);

    const handleFilter = (e) => {
        setFilters(f => ({ ...f, [e.target.name]: e.target.value }));
        setPage(1);
    };

    const handleSearch = (e) => {
        if (e.key === 'Enter' || e.target.value === '') {
            setFilters(f => ({ ...f, search: e.target.value }));
            setPage(1);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this lead? This action cannot be undone.')) return;
        setDeleting(id);
        try {
            await apiDeleteLead(id);
            toast.success('Lead deleted');
            load(page);
        } catch (e) {
            toast.error(e.response?.data?.message || 'Delete failed');
        } finally {
            setDeleting(null);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const res = await apiExportLeads();
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `leads_export_${Date.now()}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('CSV exported!');
        } catch {
            toast.error('Export failed');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="page">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Leads</h1>
                    <p className="page-subtitle">{total} total lead{total !== 1 ? 's' : ''}</p>
                </div>
                <div className="page-actions">
                    <button id="export-leads-btn" className="btn btn-ghost" onClick={handleExport} disabled={exporting}>
                        {exporting ? '⏳' : '📥'} Export CSV
                    </button>
                    <button id="import-leads-btn" className="btn btn-ghost" onClick={() => navigate('/import')}>
                        📤 Import CSV
                    </button>
                    <button id="add-lead-btn" className="btn btn-primary" onClick={() => navigate('/leads/new')}>
                        ➕ Add Lead
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="card" style={{ marginBottom: 20 }}>
                <div className="card-body" style={{ padding: '16px 22px' }}>
                    <div className="search-bar">
                        <input
                            id="lead-search"
                            className="search-input"
                            placeholder="🔍 Search name, phone, email…"
                            defaultValue={filters.search}
                            onKeyDown={handleSearch}
                            onBlur={handleSearch}
                        />
                        <select id="filter-status" name="status" className="filter-select" value={filters.status} onChange={handleFilter}>
                            {STATUSES.map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
                        </select>
                        <select id="filter-source" name="source" className="filter-select" value={filters.source} onChange={handleFilter}>
                            {SOURCES.map(s => <option key={s} value={s}>{s || 'All Sources'}</option>)}
                        </select>
                        {user?.role === 'admin' && (
                            <select id="filter-counselor" name="counselor_id" className="filter-select" value={filters.counselor_id} onChange={handleFilter}>
                                <option value="">All Counselors</option>
                                {counselors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        )}
                        {(filters.search || filters.status || filters.source || filters.counselor_id) && (
                            <button className="btn btn-ghost btn-sm" onClick={() => { setFilters({ search: '', status: '', source: '', counselor_id: '' }); setPage(1); }}>
                                ✕ Clear
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card">
                {loading ? (
                    <div className="loading-center" style={{ padding: '60px' }}><div className="spinner" /><span>Loading leads…</span></div>
                ) : leads.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">👥</div>
                        <p>No leads found. Try adjusting filters or <button className="btn btn-ghost btn-sm" onClick={() => navigate('/leads/new')}>add one</button>.</p>
                    </div>
                ) : (
                    <>
                        <div className="table-wrapper">
                            <table className="data-table" id="leads-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Phone</th>
                                        <th>Course</th>
                                        <th>Source</th>
                                        <th>Status</th>
                                        {user?.role === 'admin' && <th>Counselor</th>}
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leads.map((l, i) => (
                                        <tr key={l.id} id={`lead-row-${l.id}`}>
                                            <td style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                                                {(page - 1) * 15 + i + 1}
                                            </td>
                                            <td>
                                                <strong
                                                    style={{ cursor: 'pointer', color: 'var(--accent-light)' }}
                                                    onClick={() => navigate(`/leads/${l.id}`)}
                                                >
                                                    {l.name}
                                                </strong>
                                                {l.email && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{l.email}</div>}
                                            </td>
                                            <td>{l.phone}</td>
                                            <td>{l.course}</td>
                                            <td>{l.source}</td>
                                            <td>
                                                <span className={`badge badge--${l.status.replace(/\s+/g, '-')}`}>{l.status}</span>
                                            </td>
                                            {user?.role === 'admin' && (
                                                <td>{l.counselor_name || <span style={{ color: 'var(--text-muted)' }}>Unassigned</span>}</td>
                                            )}
                                            <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(l.created_at)}</td>
                                            <td>
                                                <div className="action-cell">
                                                    <button
                                                        className="btn btn-ghost btn-sm btn-icon"
                                                        title="View"
                                                        onClick={() => navigate(`/leads/${l.id}`)}
                                                        id={`view-lead-${l.id}`}
                                                    >👁</button>
                                                    <button
                                                        className="btn btn-ghost btn-sm btn-icon"
                                                        title="Edit"
                                                        onClick={() => navigate(`/leads/${l.id}/edit`)}
                                                        id={`edit-lead-${l.id}`}
                                                    >✏️</button>
                                                    {user?.role === 'admin' && (
                                                        <button
                                                            className="btn btn-danger btn-sm btn-icon"
                                                            title="Delete"
                                                            onClick={() => handleDelete(l.id)}
                                                            disabled={deleting === l.id}
                                                            id={`delete-lead-${l.id}`}
                                                        >🗑</button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="pagination">
                            <span className="pagination-info">Page {page} of {pages} — {total} leads</span>
                            <button className="pagination-btn" disabled={page <= 1} onClick={() => setPage(1)}>«</button>
                            <button className="pagination-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹ Prev</button>
                            {Array.from({ length: Math.min(pages, 5) }, (_, i) => {
                                const start = Math.max(1, page - 2);
                                const pg = start + i;
                                if (pg > pages) return null;
                                return (
                                    <button key={pg} className={`pagination-btn${pg === page ? ' active' : ''}`} onClick={() => setPage(pg)}>{pg}</button>
                                );
                            })}
                            <button className="pagination-btn" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next ›</button>
                            <button className="pagination-btn" disabled={page >= pages} onClick={() => setPage(pages)}>»</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
